import React, { useState, useRef } from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, X, Award } from 'lucide-react';
import Avatar from '../common/Avatar';
import PostCarousel from './PostCarousel';
import RoundRecapCard from '../scorecards/RoundRecapCard';
import GameRecapCard from '../scorecards/GameRecapCard';
import { getRelativeTime } from '../../utils/formatting';
import { getUserReactionType, getReactionTotal } from '../../utils/reactions';
import { applyWordFilter } from '../../utils/content';
import SecurityUtils from '../../utils/security';

// ─── Text-only gradient backgrounds ────────────────────────────────────────
const TEXT_GRADIENTS = [
  'from-[#16a34a] to-[#15803d]',
  'from-[#166534] to-[#14532d]',
  'from-[#16a34a] to-[#0f766e]',
  'from-[#15803d] to-[#1e3a5f]',
  'from-[#166534] to-[#1e293b]',
];

const getGradient = (id) => {
  const idx = id ? id.charCodeAt(id.length - 1) % TEXT_GRADIENTS.length : 0;
  return TEXT_GRADIENTS[idx];
};

// ─── Inline caption / comment text with tappable hashtags & mentions ───────
const RichText = ({ text, onHashtag, onMention }) => {
  if (!text) return null;
  const tokens = text.split(/(\s+)/);
  return (
    <>
      {tokens.map((token, i) => {
        if (/^#\w+/.test(token)) {
          return (
            <span key={i} className="text-blue-400 cursor-pointer" onClick={() => onHashtag(token)}>
              {token}
            </span>
          );
        }
        if (/^@\w+/.test(token)) {
          const handle = token.replace(/^@/, '').replace(/\W.*$/, '');
          return (
            <span key={i} className="text-blue-400 cursor-pointer" onClick={() => onMention(handle)}>
              {token}
            </span>
          );
        }
        return <span key={i}>{token}</span>;
      })}
    </>
  );
};

// ─── PostCard ───────────────────────────────────────────────────────────────
const PostCard = ({
  post,
  author,
  currentUser,
  users,
  wordFilters,
  isEditing,
  editContent,
  onEditChange,
  onSaveEdit,
  onCancelEdit,
  onReaction,
  onSave,
  onAddComment,
  onLikeComment,
  onDeleteComment,
  onEdit,
  onDelete,
  onReport,
  onShare,
  onViewProfile,
  onHashtag,
  onOpenComments,
  toggleFollow,
  onNotInterested,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [localComment, setLocalComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenIdx, setFullscreenIdx] = useState(0);
  const [fsSwipeStartX, setFsSwipeStartX] = useState(null);
  const lastTapRef = useRef(0);
  const commentInputRef = useRef(null);

  // Unified media array (mirrors PostCarousel logic)
  const allMedia = [
    ...(post.video?.url ? [{ type: 'video', url: post.video.url, thumbnail: post.video.thumbnail }] : []),
    ...(post.images || []).map(url => ({ type: 'image', url })),
  ];

  const isOwnPost = post.userId === currentUser.id;
  const isFollowing = currentUser.following?.includes(post.userId);
  const isLiked = getUserReactionType(post, currentUser.id) !== null;
  const likeCount = getReactionTotal(post);
  const isSaved = (post.saves || []).includes(currentUser.id);
  const hasMedia = (post.images?.length > 0) || !!post.video?.url;
  const isRecap = post.type === 'roundRecap' || post.type === 'gameRecap';
  const isTextOnly = !hasMedia && !isRecap;

  const topComments = (post.comments || []).filter(c => !c.replyToCommentId);
  const previewComments = topComments.slice(0, 2);
  const totalComments = (post.comments || []).length;

  // ── Open fullscreen modal ───────────────────────────────────────────────
  const handleOpenFullscreen = (idx) => {
    setFullscreenIdx(idx || 0);
    setFullscreenOpen(true);
  };

  // ── Fullscreen swipe ────────────────────────────────────────────────────
  const handleFsSwipeStart = (e) => setFsSwipeStartX(e.touches[0].clientX);
  const handleFsSwipeEnd = (e) => {
    if (fsSwipeStartX === null) return;
    const diff = fsSwipeStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && fullscreenIdx < allMedia.length - 1) setFullscreenIdx(i => i + 1);
      else if (diff < 0 && fullscreenIdx > 0) setFullscreenIdx(i => i - 1);
    }
    setFsSwipeStartX(null);
  };

  // ── Double-tap to like ──────────────────────────────────────────────────
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (!isLiked) onReaction(post.id, 'heart');
    }
    lastTapRef.current = now;
  };

  // ── Mention suggestions in inline comment input ─────────────────────────
  const checkMention = (value, cursorPos) => {
    const before = value.slice(0, cursorPos);
    const match = before.match(/@(\w+)$/);
    if (match) {
      const q = match[1].toLowerCase();
      setMentionSuggestions(
        users.filter(u => u.handle && u.role !== 'admin' && u.role !== 'superadmin' && u.handle.toLowerCase().startsWith(q)).slice(0, 6)
      );
    } else {
      setMentionSuggestions([]);
    }
  };

  const applyMention = (handle) => {
    const el = commentInputRef.current;
    const cursor = el ? el.selectionStart : localComment.length;
    const before = localComment.slice(0, cursor).replace(/@\w+$/, `@${handle} `);
    const after = localComment.slice(cursor);
    setLocalComment(before + after);
    setMentionSuggestions([]);
    setTimeout(() => {
      if (el) { el.focus(); el.setSelectionRange(before.length, before.length); }
    }, 0);
  };

  const handleSubmitComment = () => {
    if (!localComment.trim()) return;
    const sanitized = SecurityUtils.sanitizeText(localComment.trim());
    onAddComment(post.id, sanitized, replyTo);
    setLocalComment('');
    setReplyTo(null);
    setMentionSuggestions([]);
  };

  const handleMentionNavigate = (handle) => {
    const u = users.find(u2 => u2.handle === handle);
    if (u) onViewProfile(u.id);
  };

  // ── Why are you seeing this ─────────────────────────────────────────────
  const getWhyReasons = () => {
    const reasons = [];
    const isFollowing = currentUser.following?.includes(post.userId);
    const engagement = getReactionTotal(post) + (post.comments?.length || 0) + (post.saves?.length || 0);
    const ageHours = (Date.now() - new Date(post.timestamp).getTime()) / 3600000;

    if (isFollowing) {
      reasons.push(`You follow @${author?.handle}.`);
    }
    if (engagement >= 10) {
      reasons.push('This post has high engagement in the Loft community.');
    } else if (engagement >= 3) {
      reasons.push('Other golfers in your network are engaging with this post.');
    }
    if (ageHours < 3) {
      reasons.push('This is a recent post.');
    }
    if (reasons.length === 0) {
      reasons.push('This post was recommended based on activity in your network.');
    }
    return reasons;
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <article id={`post-${post.id}`} className="bg-black border-b-[16px] border-gray-900">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center space-x-2.5 min-w-0 flex-1">
          <button onClick={() => onViewProfile(author?.id)} className="flex-shrink-0">
            <Avatar user={author} size="sm" />
          </button>
          <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 min-w-0">
            <button onClick={() => onViewProfile(author?.id)} className="text-sm font-bold text-white leading-none">
              {author?.handle || 'user'}
            </button>
            {author?.isCoach && (
              <span className="flex items-center text-xs bg-blue-600 text-pure-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                <Award className="w-3 h-3 mr-0.5" />Coach
              </span>
            )}
            {post.type === 'lookingToPlay' && (
              <span className="text-xs border border-gray-600 text-gray-400 px-1.5 py-0.5 rounded-full flex-shrink-0">Looking to Play</span>
            )}
          </div>
          {!isOwnPost && !isFollowing && (
            <>
              <span className="text-gray-600 text-sm flex-shrink-0">·</span>
              <button onClick={() => toggleFollow(author?.id)} className="text-sm font-bold text-[#16a34a] hover:text-[#15803d] flex-shrink-0">
                Follow
              </button>
            </>
          )}
        </div>
        <button onClick={() => setShowMenu(true)} className="pl-2 text-white flex-shrink-0">
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>

      {/* ── Body ── */}
      {isEditing ? (
        <div className="px-3 pb-3">
          <textarea
            value={editContent}
            onChange={e => onEditChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-gray-900 resize-none text-sm focus:ring-2 focus:ring-[#16a34a] outline-none"
            rows={4}
            autoFocus
          />
          <div className="flex justify-end space-x-2 mt-2">
            <button onClick={onCancelEdit} className="px-3 py-1.5 text-sm text-gray-400 border border-gray-700 rounded-lg hover:text-white">Cancel</button>
            <button onClick={() => onSaveEdit(post.id)} className="px-3 py-1.5 text-sm font-bold text-white bg-[#16a34a] hover:bg-[#15803d] rounded-lg">Save</button>
          </div>
        </div>
      ) : (
        <>
          {/* Photos / Video carousel */}
          {hasMedia && (
            <div onClick={handleDoubleTap}>
              <PostCarousel
                images={post.images}
                video={post.video}
                onOpenItem={handleOpenFullscreen}
              />
            </div>
          )}

          {/* Text-only post: gradient background */}
          {isTextOnly && (
            <div
              className={`w-full flex items-center justify-center p-8 bg-gradient-to-br ${getGradient(post.id)} cursor-pointer`}
              style={{ minHeight: '240px' }}
              onClick={handleDoubleTap}
            >
              <p className="text-pure-white text-lg font-semibold text-center leading-relaxed break-words max-w-xs">
                {applyWordFilter(post.content, wordFilters)}
              </p>
            </div>
          )}

          {/* Recap posts: show RoundRecapCard / GameRecapCard (with any photos above) */}
          {isRecap && !hasMedia && (
            <div className={`bg-gradient-to-br ${getGradient(post.id)} p-1`}>
              {post.type === 'roundRecap' && post.roundRecap && <RoundRecapCard roundRecap={post.roundRecap} />}
              {post.type === 'gameRecap' && post.gameRecap && <GameRecapCard gameRecap={post.gameRecap} />}
            </div>
          )}

          {/* ── Action row ── */}
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
            <div className="flex items-center space-x-4">
              {/* Like */}
              <button
                onClick={() => onReaction(post.id, 'heart')}
                className={`flex items-center space-x-1.5 transition-transform active:scale-90 ${isLiked ? 'text-red-500' : 'text-white hover:text-gray-400'}`}
              >
                <Heart className={`w-6 h-6 ${isLiked ? 'fill-red-500' : ''}`} />
                {likeCount > 0 && <span className="text-sm font-semibold">{likeCount.toLocaleString()}</span>}
              </button>
              {/* Comment */}
              <button onClick={() => onOpenComments(post.id)} className="flex items-center space-x-1.5 text-white hover:text-gray-400">
                <MessageCircle className="w-6 h-6" />
                {totalComments > 0 && <span className="text-sm font-semibold">{totalComments}</span>}
              </button>
              {/* Share */}
              <button onClick={() => onShare(post.id)} className="flex items-center space-x-1.5 text-white hover:text-gray-400">
                <Send className="w-6 h-6" />
                {(post.shares || 0) > 0 && <span className="text-sm font-semibold">{post.shares}</span>}
              </button>
            </div>
            <button
              onClick={() => onSave(post.id)}
              className={`transition-transform active:scale-90 ${isSaved ? 'text-white' : 'text-white hover:text-gray-400'}`}
            >
              <Bookmark className={`w-6 h-6 ${isSaved ? 'fill-white' : ''}`} />
            </button>
          </div>

          {/* ── Below action row ── */}
          <div className="px-3 pb-2 space-y-0.5">

            {/* Caption — only for non-text-only posts (text-only shows content in the gradient) */}
            {!isTextOnly && post.content && (
              <p className="text-sm text-white leading-snug">
                <button onClick={() => onViewProfile(author?.id)} className="font-bold text-white hover:text-gray-300 mr-1">
                  {author?.handle}
                </button>
                <RichText
                  text={applyWordFilter(post.content, wordFilters)}
                  onHashtag={onHashtag}
                  onMention={handleMentionNavigate}
                />
                {post.edited && <span className="text-xs text-gray-500 ml-1">(edited)</span>}
              </p>
            )}

            {/* Recap cards when post also has photos */}
            {isRecap && hasMedia && (
              <div className="mt-1">
                {post.type === 'roundRecap' && post.roundRecap && <RoundRecapCard roundRecap={post.roundRecap} />}
                {post.type === 'gameRecap' && post.gameRecap && <GameRecapCard gameRecap={post.gameRecap} />}
              </div>
            )}

            {/* Marketplace info */}
            {post.marketplace?.title && (
              <div className="bg-gray-900 rounded-lg p-2.5 mt-1">
                <p className="text-sm font-bold text-white">{post.marketplace.title}</p>
                <p className="text-sm text-gray-400">${post.marketplace.price} · {post.marketplace.condition}</p>
              </div>
            )}

            {/* "View all X comments" link */}
            {totalComments > 2 && (
              <button
                onClick={() => onOpenComments(post.id)}
                className="text-sm text-gray-400 hover:text-white block"
              >
                View all {totalComments} comments
              </button>
            )}

            {/* Preview: top 2 comments */}
            {previewComments.map(comment => {
              const commenter = users.find(u => u.id === comment.userId);
              return (
                <p key={comment.id} className="text-sm text-white leading-snug line-clamp-2">
                  <button
                    onClick={() => onViewProfile(commenter?.id)}
                    className="font-bold mr-1 hover:text-gray-300"
                  >
                    {commenter?.handle || 'user'}
                  </button>
                  <RichText
                    text={applyWordFilter(comment.content, wordFilters)}
                    onHashtag={onHashtag}
                    onMention={handleMentionNavigate}
                  />
                </p>
              );
            })}

            {/* Timestamp */}
            <p className="text-xs text-gray-500 uppercase tracking-wide pt-0.5">
              {getRelativeTime(post.timestamp)}
            </p>
          </div>

          {/* ── Inline comment input ── */}
          <div className="relative border-t border-gray-900">
            <div className="flex items-center px-3 py-2 space-x-2">
              <Avatar user={currentUser} size="xs" />
              {replyTo && (
                <button onClick={() => { setReplyTo(null); setLocalComment(''); }} className="text-gray-500 hover:text-white flex-shrink-0">
                  <X className="w-3 h-3" />
                </button>
              )}
              <input
                ref={commentInputRef}
                type="text"
                value={localComment}
                onChange={e => { setLocalComment(e.target.value); checkMention(e.target.value, e.target.selectionStart); }}
                onBlur={() => setTimeout(() => setMentionSuggestions([]), 150)}
                onKeyPress={e => e.key === 'Enter' && handleSubmitComment()}
                placeholder={replyTo ? `Reply to @${replyTo.handle}...` : 'Add a comment...'}
                className="flex-1 text-sm text-white bg-transparent outline-none placeholder-gray-600 min-w-0"
              />
              {localComment.trim() && (
                <button onClick={handleSubmitComment} className="text-sm font-bold text-[#16a34a] hover:text-[#15803d] flex-shrink-0">
                  Post
                </button>
              )}
            </div>
            {mentionSuggestions.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 bg-gray-900 border border-gray-700 rounded-t-xl overflow-hidden shadow-xl z-20">
                {mentionSuggestions.map(u => (
                  <button
                    key={u.id}
                    onMouseDown={e => { e.preventDefault(); applyMention(u.handle); }}
                    className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-gray-800 text-left"
                  >
                    <Avatar user={u} size="xs" />
                    <span className="text-sm text-white font-bold">@{u.handle}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Fullscreen media modal ── */}
      {fullscreenOpen && allMedia.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black flex flex-col"
          onTouchStart={handleFsSwipeStart}
          onTouchEnd={handleFsSwipeEnd}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
            <button onClick={() => setFullscreenOpen(false)} className="text-white p-1">
              <X className="w-6 h-6" />
            </button>
            {allMedia.length > 1 && (
              <span className="text-white text-sm font-medium">
                {fullscreenIdx + 1}/{allMedia.length}
              </span>
            )}
            <div className="w-8" />
          </div>

          {/* Media */}
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            {allMedia[fullscreenIdx].type === 'video' ? (
              <video
                src={allMedia[fullscreenIdx].url}
                poster={allMedia[fullscreenIdx].thumbnail}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
              />
            ) : (
              <img
                src={allMedia[fullscreenIdx].url}
                alt=""
                className="w-full h-full object-contain"
                draggable={false}
              />
            )}
          </div>

          {/* Dot indicators */}
          {allMedia.length > 1 && (
            <div className="flex justify-center space-x-1.5 py-4 flex-shrink-0">
              {allMedia.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setFullscreenIdx(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === fullscreenIdx ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Why are you seeing this (bottom sheet) ── */}
      {showWhy && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setShowWhy(false)}>
          <div
            className="w-full bg-gray-900 rounded-t-2xl border-t border-gray-700 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-600 rounded-full mx-auto mt-3 mb-3" />
            <div className="px-5 pb-2">
              <p className="text-sm font-bold text-white mb-3">Why you're seeing this post</p>
              <ul className="space-y-2 mb-4">
                {getWhyReasons().map((reason, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-gray-300">
                    <span className="text-[#16a34a] mt-0.5 flex-shrink-0">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={() => setShowWhy(false)} className="w-full px-5 py-3.5 text-center text-gray-400 hover:bg-gray-800 text-sm border-t border-gray-800">
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Three-dot menu (bottom sheet) ── */}
      {showMenu && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setShowMenu(false)}>
          <div
            className="w-full bg-gray-900 rounded-t-2xl border-t border-gray-700 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-600 rounded-full mx-auto mt-3 mb-1" />
            {isOwnPost ? (
              <>
                <button onClick={() => { onEdit(post.id); setShowMenu(false); }} className="w-full px-5 py-3.5 text-left text-white hover:bg-gray-800 text-sm font-medium border-b border-gray-800">
                  Edit
                </button>
                <button onClick={() => { onDelete(post.id); setShowMenu(false); }} className="w-full px-5 py-3.5 text-left text-red-400 hover:bg-gray-800 text-sm font-medium">
                  Delete
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { onSave(post.id); setShowMenu(false); }} className="w-full px-5 py-3.5 text-left text-white hover:bg-gray-800 text-sm font-medium border-b border-gray-800">
                  {isSaved ? 'Remove from saved' : 'Save'}
                </button>
                <button onClick={() => { setShowMenu(false); setShowWhy(true); }} className="w-full px-5 py-3.5 text-left text-white hover:bg-gray-800 text-sm font-medium border-b border-gray-800">
                  Why are you seeing this
                </button>
                <button onClick={() => { onNotInterested?.(post); setShowMenu(false); }} className="w-full px-5 py-3.5 text-left text-white hover:bg-gray-800 text-sm font-medium border-b border-gray-800">
                  Not interested
                </button>
                <button onClick={() => { onReport(post.id, post.userId); setShowMenu(false); }} className="w-full px-5 py-3.5 text-left text-red-400 hover:bg-gray-800 text-sm font-medium">
                  Report
                </button>
              </>
            )}
            <button onClick={() => setShowMenu(false)} className="w-full px-5 py-3.5 text-center text-gray-400 hover:bg-gray-800 text-sm border-t border-gray-800">
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export default PostCard;
